import React, {FC, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import styles from './RouteTile.module.scss';
import {
    AIStarIcon,
    CheckBox,
    Flex,
    Icon,
    PriorityIcon,
    Scrollbar
} from '@priority-software/priority-style-react';
import {RouteTitleSection} from './RouteTitleSection';
import {formsStore, Record} from '../../../../stores/Form';
import {
    ColumnsNamesForLocationsSubform,
    ColumnsNamesForRoutes,
    DLVADDRESSSUM,
    DLVADDRESSSUMDET,
    DLVTRACKI,
    NUMLINE,
    OPTDATE,
    OPTIMIZED,
    TOTDOCS,
    TOTORDERS
} from '../../constants';
import {TableWithDnd} from '../Table';
import {LocationType, MapSettings, MovingLocation, RouteType} from '../../types';
import {CircularLoader} from '../../../../components/CircularLoader/CircularLoader';
import {useScrollWrapDimensions} from '../../../../hooks/customScroll/useScrollWrapDimensions';
import {exitSubForm} from '../../../Form/actions/form';
import {AdvancedDeliveryContext} from '../../reducer/context';
import {Point} from '../Table/Point';
import _ from 'lodash';
import {isRTL} from '../../../../Localization';
import {RouteMenu} from '../RouteMenu';
import {useDNDContext} from '../Table/DNDController/useDNDContext';
import {fetchSubform} from '../../actions/action';
import {RouteTotalsSection} from './RouteTotalsSection';
import {startAndGetSubformColumns} from '../../../Form/actions/batchedActions/standAloneFormBatch';
import {priority} from '@priority-software/priority-proto-api/lib';
import {DropdownItemData} from '@priority-software/priority-style-react/dist/components/DropdownV2/ChooseDropdown/ChooseDropdown';
import cx from 'classnames';
import {texts} from '../../texts';
import {ModernTooltip} from '../../../../components/Common/Tooltip/ModernTooltip';

export type RouteTileProps = {
    route: RouteType;
    setIsScreenLocked: (isLocked: boolean) => void;
    isSelected: boolean;
    toggleRouteSelect: (route: RouteType, mapSettings: MapSettings) => void;
    onSelectRows?: (rows: number[], tableId: string, route?: RouteType) => void;
    isRouteTileLoading: boolean;
    setIsRouteTileLoading: (value: boolean) => void;
    portalId?: string;
    titleColumns: ColumnsNamesForRoutes[];
    isRouteGroupLoading: boolean;
    setIsRouteGroupLoading: (value: boolean) => void;
    isRouteReloading: boolean;
    setIsRouteReloading: (value: boolean) => void;
    totalsColumns: ColumnsNamesForRoutes[];
    tableColumns: string[];
    cachedColumns?: priority.netitems.form.IColumn[];
    setDeliveryColumnsSetting: (columns?: priority.netitems.form.IColumn[]) => void;
    docTypeFilter: DropdownItemData[];
    mapSettings: MapSettings;
};

const LOADER_PADDING = 368;
const SUMMARY_PADDING = 56;
const INFO_PADDING = 36;
const CELL_HEIGHT = 32; // equals to Table component cell height defind in scss file "../Table/Table.module.scss"

export const RouteTile: FC<RouteTileProps> = ({
    route,
    setIsScreenLocked,
    isSelected,
    toggleRouteSelect,
    onSelectRows,
    isRouteTileLoading,
    setIsRouteTileLoading,
    portalId,
    titleColumns,
    totalsColumns,
    tableColumns,
    isRouteGroupLoading,
    setIsRouteGroupLoading,
    setIsRouteReloading,
    isRouteReloading,
    cachedColumns,
    setDeliveryColumnsSetting,
    docTypeFilter,
    mapSettings
}) => {
    const {
        setRouteLocationsAndForm,
        setRouteLocationFormLoading,
        setIsCollapsableRouteOpen,
        setLocationSubformOpen,
        setRouteLocationReload,
        routesForm,
        routes,
        setLocationItems
    } = useContext(AdvancedDeliveryContext);

    const ref = useRef();

    const isRtl = isRTL();

    const isLoading = route.locationSubformLoading;
    const isCollapsableOpen = route.isCollapsableOpen;

    const id = useMemo(() => Number.parseInt(route.id), [route.id]);
    const [isLocationLoading, setIsLocationLoading] = useState(false);

    const [selectedRows, setSelectedRows] = useState<Record[]>([]);

    const [isTriggerDisabled, setIsTriggerDisabled] = useState(false);

    const {setWrapRef, maxHeight: scrollMaxHeight, maxWidth} = useScrollWrapDimensions();

    useEffect(() => {
        if (isRtl) {
            (ref.current as any).scrollToRight();
        } else {
            (ref.current as any).scrollToLeft();
        }
    }, [isRtl, isCollapsableOpen, route.locationSubformOpen, maxWidth]);

    const openRouteDetails = useCallback(() => {
        if (!isRouteTileLoading) {
            setIsRouteTileLoading(true);
            if (route.locationSubform === null || route.locationSubformReload || !cachedColumns) {
                routesForm.setCurrentRow(id);
                setRouteLocationFormLoading(id, true);
                fetchSubform(routesForm, DLVADDRESSSUM).then(async form => {
                    const locations: LocationType[] = form.records
                        .filter(x => !x.isNew)
                        .map(location => ({
                            id: location.recordIndex,
                            data: location,
                            itemsSubform: null
                        }));

                    if (locations?.length && !cachedColumns) {
                        const data = await startAndGetSubformColumns(
                            form,
                            DLVADDRESSSUMDET,
                            [],
                            true
                        );
                        await setDeliveryColumnsSetting(data.Columns);
                        await exitSubForm(form.session);
                    }
                    await exitSubForm(form.session);
                    formsStore.getScreenBySession(routesForm.session).setActiveLevel(0);
                    setRouteLocationsAndForm(id, locations, form);
                    setRouteLocationFormLoading(id, false);
                    setLocationSubformOpen(id, true);
                    setRouteLocationReload(id, false);
                    setIsLocationLoading(false);
                    setIsRouteReloading(false);
                    setIsRouteTileLoading(false);
                });
            } else {
                setLocationSubformOpen(id, true);
                setIsRouteTileLoading(false);
            }
        }
    }, [route, routesForm, id, cachedColumns]);

    const closeRouteDetails = useCallback(() => {
        setLocationSubformOpen(id, false);
    }, [id]);

    useEffect(() => {
        if (route.locationSubformReload && !isRouteReloading) {
            const firstReloadingRoute = routes.find(x => x.locationSubformReload);
            if (firstReloadingRoute.id === route.id) {
                setIsLocationLoading(true);
                setIsCollapsableRouteOpen(id, true);
                setIsRouteReloading(true);
                setSelectedRows([]);
                openRouteDetails();
            }
        }
    }, [route.locationSubformReload, isRouteReloading]);

    useEffect(() => {
        const hasNoDocs = !route?.data.getField(routesForm.getColumnByName(TOTDOCS)?.ID as number)
            .currentValue;
        const hasNoOrders = !route?.data.getField(
            routesForm.getColumnByName(TOTORDERS)?.ID as number
        ).currentValue;

        setIsTriggerDisabled(hasNoDocs && hasNoOrders);
    }, [route.data, isRouteTileLoading, route.locationSubformReload]);

    useEffect(() => {
        if (!isCollapsableOpen) {
            closeRouteDetails();
        }
    }, [isCollapsableOpen]);

    const isOpenDetails = route.locationSubformOpen;

    const getTableColumns = useCallback(() => {
        return _.sortBy(cachedColumns?.filter(x => tableColumns.includes(x.Name)) || [], [
            o => tableColumns.findIndex(x => x === o.Name)
        ]);
    }, [tableColumns, cachedColumns]);

    const tableRecords = useMemo(() => {
        if (
            !isOpenDetails ||
            !route.locations ||
            route.locations.length > route.locationSubform.records.length
        ) {
            return [];
        }
        let counter = 0;

        const items = route.locations
            ?.map((x, i) => {
                counter++;
                return [
                    new Record(
                        route.locationSubform,
                        route.locationSubform.records[i].properties,
                        -counter
                    ),
                    ...(x.items?.map(y => {
                        y.data
                            .getField(
                                x.itemsSubform.getColumnByName(
                                    ColumnsNamesForLocationsSubform.DISTRORDER
                                ).ID as number
                            )
                            .setCurrentValue(i.toString());
                        return y.data;
                    }) || [])
                ];
            })
            .reduce((prev, curr) => [...prev, ...curr], []);
        counter = 1;

        return items?.map((x, i) => {
            if (x.recordIndex >= 0) {
                x.recordIndex = counter;
                counter++;
            }
            return x;
        });
    }, [route, route.locations]);

    const filteredTableRecords = useMemo(() => {
        const notNewTableRecords = tableRecords?.filter(r => !r.isNew);
        const isFilterActive = docTypeFilter.length === 1;
        if (isFilterActive) {
            return notNewTableRecords?.filter(
                r =>
                    r.recordIndex < 0 ||
                    (docTypeFilter[0].value === 'O'
                        ? r.getField(r.parentForm.getColumnByName('DOCCODE').id).origValue === 'O'
                        : r.getField(r.parentForm.getColumnByName('DOCCODE').id).origValue !== 'O')
            );
        }
        return notNewTableRecords;
    }, [tableRecords, isOpenDetails, docTypeFilter]);

    const handleLocationLoading = async (index: number) => {
        const realIndex = Math.abs(index) - 1;
        setIsRouteGroupLoading(true);

        if (!route.locations[realIndex].itemsSubform) {
            setIsLocationLoading(true);

            routesForm.setCurrentRow(id);
            setRouteLocationFormLoading(id, true);

            const form = await fetchSubform(routesForm, DLVADDRESSSUM);

            form.setCurrentRow(route.locations[realIndex].id);
            const subform = await fetchSubform(form, DLVADDRESSSUMDET, [DLVTRACKI]);

            const locations = [...route.locations];

            locations[realIndex].itemsSubform = subform;
            const items = subform.records
                .filter(x => !x.isNew)
                .map(item => ({
                    id: item.recordIndex,
                    data: item
                }));
            await exitSubForm(subform.session);
            await exitSubForm(form.session);
            formsStore.getScreenBySession(routesForm.session).setActiveLevel(0);
            setLocationItems(id, route.locations[realIndex].id, items);

            setIsLocationLoading(false);
            setRouteLocationFormLoading(id, false);
        }

        setIsRouteGroupLoading(false);
    };

    const dndState = useDNDContext();
    const scrollWrapperOuterRef = useRef(null);

    useEffect(() => {
        if (
            dndState &&
            !dndState.isMoving &&
            dndState.coordinates &&
            scrollWrapperOuterRef.current
        ) {
            const wrapperRect = scrollWrapperOuterRef.current.getBoundingClientRect();
            if (
                wrapperRect.left <= dndState.coordinates[0] &&
                wrapperRect.top <= dndState.coordinates[1] &&
                wrapperRect.bottom >= dndState.coordinates[1] &&
                wrapperRect.right >= dndState.coordinates[0]
            ) {
                dndState.setTo({
                    id: (MovingLocation.table2 +
                        route.data.getField(routesForm.getColumnByName('DLVTRACKNUM')?.id)
                            ?.currentValue) as string
                });
            }
        }
    }, [dndState?.isMoving, dndState?.coordinates, route]);

    const getPointLabel = useCallback((record: Record, records: Record[], index: number) => {
        //we want to pass parent record index to the cell renderer
        function findNegativeRecordIndex(records: Record[], startIndex: number): Record | null {
            for (let i = startIndex; i >= 0; i--) {
                if (records[i].recordIndex < 0) {
                    return records[i];
                }
            }
            return null;
        }
        return record.recordIndex < 0
            ? (-record.recordIndex).toString()
            : (-findNegativeRecordIndex(records, index)?.recordIndex).toString();
    }, []);

    const routeSelectHandler = useCallback(async () => {
        setIsScreenLocked(true);
        await toggleRouteSelect(route, mapSettings);
        setIsScreenLocked(false);
    }, [route, toggleRouteSelect]);

    const getInfoTableHeight = useCallback((): number => {
        const maxHeight = 230; // ((1 Header + 6 rows) * CELL_HEIGHT) + 6px;
        const numOfRecords = filteredTableRecords.length;
        if (numOfRecords === 0) {
            return 0;
        } else {
            const currentHeight = (filteredTableRecords.length + 1) * CELL_HEIGHT + 6;
            return currentHeight < maxHeight ? currentHeight : maxHeight;
        }
    }, [filteredTableRecords]);

    const getTriggerIcon = useCallback(() => {
        if (isTriggerDisabled || !(route.locationSubformLoading || isOpenDetails)) {
            return isRtl ? PriorityIcon.arrowLeft : PriorityIcon.arrowRight;
        } else {
            return PriorityIcon.arrowDown;
        }
    }, [isTriggerDisabled, route.locationSubformLoading, isOpenDetails]);

    useEffect(() => {
        if (isOpenDetails && !isCollapsableOpen) {
            setIsCollapsableRouteOpen(Number.parseInt(route.id), true);
        }
    }, [isOpenDetails]);

    const routeOptimizedDate = useMemo(() => {
        const OptDateColumnId = route.data.parentForm.columns.find(x => x.Name === OPTDATE)?.id;
        const OptimizedColumnId = route.data.parentForm.columns.find(x => x.Name === OPTIMIZED)?.id;

        if (OptDateColumnId === undefined || OptimizedColumnId === undefined) {
            return undefined;
        }

        return route.data.getField(OptimizedColumnId).Value.Boolean
            ? route.data.getField(OptDateColumnId).currentValue
            : undefined;
    }, [route]);

    return (
        <Flex
            ref={setWrapRef}
            componentClasses={styles.wrapper}
            style={{borderColor: route.color}}
            alignItems={'flex-start'}
            justifyContent={'flex-start'}
            direction={'column'}
        >
            <Scrollbar
                scrollbarRef={ref}
                componentClasses={styles.scroll}
                rtl={isRtl}
                autoHide
                autoHeight={true}
                autoHeightMax={scrollMaxHeight}
                autoHeightMin={
                    isOpenDetails || isCollapsableOpen || isLoading
                        ? isOpenDetails || isLoading
                            ? INFO_PADDING + SUMMARY_PADDING
                            : isCollapsableOpen
                            ? INFO_PADDING + SUMMARY_PADDING
                            : LOADER_PADDING
                        : INFO_PADDING
                }
                style={{width: '100%', height: '100%'}}
            >
                <div ref={scrollWrapperOuterRef} className={styles.scrollWrapperOuter}>
                    <div className={styles.scrollWrapperInner}>
                        <Flex
                            componentClasses={styles.info}
                            style={{
                                [isRtl ? 'paddingLeft' : 'paddingRight']: routeOptimizedDate
                                    ? '32px'
                                    : '8px'
                            }}
                            direction={'row'}
                            justifyContent={'flex-start'}
                        >
                            <CheckBox
                                checked={isSelected}
                                onChange={routeSelectHandler}
                                componentClasses={styles.checkbox}
                            />
                            <Icon
                                onClick={() =>
                                    setIsCollapsableRouteOpen(id, !route.isCollapsableOpen)
                                }
                                componentClasses={styles.infoCollapse}
                                icon={
                                    !route.isCollapsableOpen
                                        ? isRtl
                                            ? PriorityIcon.arrowLeft
                                            : PriorityIcon.arrowRight
                                        : PriorityIcon.arrowDown
                                }
                            />
                            <RouteTitleSection
                                routeData={route.data}
                                routesForm={routesForm}
                                colNames={titleColumns}
                            />
                        </Flex>
                        {isCollapsableOpen ? (
                            <Flex
                                direction={'column'}
                                alignItems={'flex-start'}
                                justifyContent={'flex-start'}
                                componentClasses={styles.deliveryInfoWrapper}
                                style={{height: '100%', width: '100%'}}
                            >
                                <Flex
                                    componentClasses={styles.deliveryInfo}
                                    justifyContent={'flex-start'}
                                    direction={'row'}
                                >
                                    <Icon
                                        onClick={() =>
                                            isOpenDetails ? closeRouteDetails() : openRouteDetails()
                                        }
                                        componentClasses={styles.detailsCollapse}
                                        icon={getTriggerIcon()}
                                        disabled={isRouteTileLoading || isTriggerDisabled}
                                    />
                                    <RouteTotalsSection
                                        routeData={route.data}
                                        routesForm={routesForm}
                                        colNames={totalsColumns}
                                    />
                                </Flex>
                            </Flex>
                        ) : null}
                    </div>
                </div>
            </Scrollbar>
            {isOpenDetails || route.locationSubformLoading ? (
                isOpenDetails ? (
                    route.locations.length > 0 ? (
                        <>
                            <TableWithDnd
                                height={getInfoTableHeight()}
                                id={
                                    (MovingLocation.table2 +
                                        route.data.getField(
                                            routesForm.getColumnByName('DLVTRACKNUM')?.id
                                        )?.currentValue) as string
                                }
                                onGroupOpen={handleLocationLoading}
                                columns={getTableColumns() as any}
                                groupColor={route.color}
                                getCellValue={(columnIndex, recordIndex) => {
                                    return (
                                        tableRecords.find(x => x.recordIndex === recordIndex) &&
                                        (tableRecords
                                            .find(x => x.recordIndex === recordIndex)
                                            .getField(columnIndex)?.currentValue as string)
                                    );
                                }}
                                renderPoint={(record: Record, records: Record[], index: number) => {
                                    return (
                                        <div className={styles.pointWrapper}>
                                            <Point
                                                color={route.color}
                                                text={getPointLabel(record, records, index)}
                                            />
                                        </div>
                                    );
                                }}
                                isGroupLoading={isRouteGroupLoading || isRouteTileLoading}
                                handleGrouping={false}
                                renderCell={(columnIndex, recordIndex) =>
                                    tableRecords
                                        .find(x => x.recordIndex === recordIndex)
                                        .getField(columnIndex)?.currentValue
                                }
                                getGroupInnerCount={groupIndex => {
                                    return Number.isSafeInteger(groupIndex)
                                        ? Number.parseInt(
                                              route.locations[groupIndex]?.data.getField(
                                                  route.locations[
                                                      groupIndex
                                                  ].data.parentForm.getColumnByName(NUMLINE)
                                                      .id as number
                                              ).origValue as string
                                          )
                                        : undefined;
                                }}
                                records={filteredTableRecords}
                                pinnedColumns={[]}
                                groupBy={
                                    cachedColumns?.find(
                                        x => x.Name === ColumnsNamesForLocationsSubform.DISTRORDER
                                    )?.ID as number
                                }
                                groupRenderCell={(columnIndex, groupByValue) => {
                                    let columnName = cachedColumns?.find(
                                        x => x.ID === columnIndex
                                    )?.Name;
                                    if (columnName === 'CDES') {
                                        columnName = 'REFDES';
                                    }
                                    const locationSubformColumn =
                                        route.locationSubform.getColumnByName(columnName);
                                    if (locationSubformColumn) {
                                        return route.locations[groupByValue].data.getField(
                                            locationSubformColumn.id
                                        ).currentValue;
                                    } else {
                                        return '---';
                                    }
                                }}
                                isFullyReloaded={isRouteReloading}
                                setIsScreenLocked={setIsScreenLocked}
                                selectedRows={selectedRows}
                                onSelectRows={(checkedRows, id) => {
                                    setSelectedRows(
                                        tableRecords.filter(x =>
                                            checkedRows.includes(x.recordIndex)
                                        )
                                    );
                                    onSelectRows?.(checkedRows, id, route);
                                }}
                            />
                            {isLocationLoading && (
                                <div
                                    className={cx(styles.loader, {
                                        [styles.smallContainer]: scrollMaxHeight < 200,
                                        [styles.xSmallContainer]: scrollMaxHeight < 165
                                    })}
                                >
                                    <CircularLoader />
                                </div>
                            )}
                        </>
                    ) : null
                ) : (
                    <CircularLoader />
                )
            ) : null}
            <div
                className={styles.routeMenuWrapper}
                style={{
                    width: routeOptimizedDate ? '64px' : undefined
                }}
            >
                {routeOptimizedDate && (
                    <ModernTooltip
                        tooltip={`${texts.route_optimized_tooltip}${routeOptimizedDate}`}
                        maxWidth={600}
                        wrapperClassname={styles.optimizedIndicator}
                        position="bottom"
                    >
                        <AIStarIcon />
                    </ModernTooltip>
                )}
                <RouteMenu
                    componentClasses={styles.routeMenu}
                    route={route}
                    portalId={portalId}
                    setIsScreenLocked={setIsScreenLocked}
                />
            </div>
        </Flex>
    );
};
